import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getUserExchanges, createUserExchange, updateUserExchange, testUserExchange } from '../../services/UserExchangesService';
import { getAiProviders, createAiProvider, testAiProvider } from '../../services/AiProvidersService';
import { getStrategies } from '../../services/StrategiesService';
import { getProviders } from '../../services/ExchangeService';
import { getCurrentMarket, MARKET_FUTURES } from '../../utils/MarketStorage';
import { getCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentUserId } from '../../services/TokenStorage';
import SharedExchangeSecurity from '../CredentialsGuide/SharedExchangeSecurity';
import CredentialsGuide from '../CredentialsGuide/CredentialsGuide';
import { EXCHANGE_GUIDES, AI_GUIDES } from '../../data/setupGuides';
import { AI_MODELS } from '../../utils/aiModels';
import StrategyWizard from '../StrategyWizard/StrategyWizard';

// Mesma chave do SetupWizard antigo — preserva o "não perguntar de novo" de quem já dispensou.
const STORAGE_KEY = 'setupWizardDismissed';
const AI_SKIP_KEY = 'setupAssistantAiSkipped';
const STRATEGY_SKIP_KEY = 'setupAssistantStrategySkipped';
const PASSPHRASE_EXCHANGES = ['OKX', 'BITGET'];

const STEP_LABELS = ['Corretora', 'Ambiente', 'IA (opcional)', 'Estratégia'];

// Extrai uma mensagem legível de um erro (Axios ou não), com fallback.
function _msg(v, fallback) {
    if (v == null) return fallback;
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    const candidates = [v.msg, v.message, v.error, v.body];
    for (const c of candidates) if (typeof c === 'string' && c) return c;
    return fallback;
}
function _errMsg(err, fallback) {
    const raw = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? err;
    return _msg(raw, fallback);
}

/**
 * Assistente único de configuração inicial ("Wizard 1"). Detecta o estado REAL no servidor
 * (corretora / provedor de IA / estratégia) e guia o usuário pelos passos que faltam — idempotente:
 * reabrir mostra ✓ nos passos já feitos e permite revisitar qualquer um.
 *
 * Absorve o SetupWizard antigo (que só cadastrava 1 chave via updateSettings — campo VIRTUAL que
 * não persiste mais, ver usersRepository/userModel: accessKey/secretKey/aiProvider/aiApiKey viraram
 * Sequelize.VIRTUAL, sobrescritos em memória pelo overlay de userExchanges/userAiProviders). Este
 * assistente já nasce usando as tabelas reais (userExchanges/userAiProviders), então funciona de
 * verdade pra quem está entrando pela 1ª vez.
 *
 * props:
 * - openTrigger: incrementar este número força a abertura (botão manual no Dashboard).
 * - onClose: callback opcional quando o usuário fecha o modal.
 */
function SetupAssistant({ openTrigger = 0, onClose }) {
    const market = getCurrentMarket();
    const isFutures = market === MARKET_FUTURES;
    const environment = getCurrentEnvironment();
    // Escopa as flags de "dispensado"/"pulado" por usuário — sem isso, uma conta que já configurou
    // (ou pulou um passo) faz o navegador esconder o assistente pra qualquer OUTRA conta logada nele.
    const userId = getCurrentUserId();
    const scopedStorageKey = `${STORAGE_KEY}:${userId}`;
    const scopedAiSkipKey = `${AI_SKIP_KEY}:${userId}`;
    const scopedStrategySkipKey = `${STRATEGY_SKIP_KEY}:${userId}`;

    const [visible, setVisible] = useState(false);
    const [ready, setReady] = useState(false);
    const [step, setStep] = useState(0);

    // Estado detectado no servidor.
    const [exchanges, setExchanges] = useState([]);
    const [aiProvidersList, setAiProvidersList] = useState([]);
    const [hasStrategy, setHasStrategy] = useState(false);
    const [meta, setMeta] = useState({ exchanges: [], aiProviders: [] });

    // Form: Corretora
    const [exForm, setExForm] = useState({ exchangeName: '', accessKey: '', secretKey: '', passphrase: '' });
    const [exSaving, setExSaving] = useState(false);
    const [exTesting, setExTesting] = useState(false);
    const [exResult, setExResult] = useState(null);
    const [exError, setExError] = useState('');

    // Form: Ambiente (credenciais de testnet da corretora primary)
    const [testForm, setTestForm] = useState({ testAccessKey: '', testSecretKey: '', testPassphrase: '' });
    const [testSaving, setTestSaving] = useState(false);
    const [testError, setTestError] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [wantsTestnet, setWantsTestnet] = useState(false);

    // Form: IA
    const [aiForm, setAiForm] = useState({ provider: '', apiKey: '', model: '' });
    const [aiSaving, setAiSaving] = useState(false);
    const [aiTesting, setAiTesting] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    const [aiError, setAiError] = useState('');

    const primaryExchange = exchanges.find(e => e.isPrimary) || exchanges[0] || null;
    const hasExchange = !!primaryExchange;
    const hasTestCreds = !!(primaryExchange && primaryExchange.testAccessKey);
    const hasAi = aiProvidersList.length > 0;

    function detect() {
        return Promise.all([
            getUserExchanges().catch(() => []),
            getAiProviders().catch(() => []),
            getStrategies('', 1, 100, environment, market).catch(() => ({ rows: [] })),
        ]).then(([ex, ai, stratRes]) => {
            const exList = ex || [];
            const aiList = ai || [];
            const stratHas = ((stratRes && stratRes.rows) || []).length > 0;
            setExchanges(exList);
            setAiProvidersList(aiList);
            setHasStrategy(stratHas);
            return { exList, aiList, stratHas };
        });
    }

    function firstPendingStep(exList, aiList, stratHas) {
        if (!exList || exList.length === 0) return 0;
        if (!aiList || aiList.length === 0) {
            if (!localStorage.getItem(scopedAiSkipKey)) return 2;
        }
        if (!stratHas) {
            if (!localStorage.getItem(scopedStrategySkipKey)) return 3;
        }
        return 3;
    }

    // 1ª detecção: decide se o popup automático deve aparecer (mesma condição do SetupWizard antigo:
    // sem corretora configurada e não dispensado).
    useEffect(() => {
        let alive = true;
        detect().then(({ exList }) => {
            if (!alive) return;
            setReady(true);
            const dismissed = localStorage.getItem(scopedStorageKey);
            if (!dismissed && exList.length === 0) {
                setStep(0);
                setVisible(true);
            }
        });
        getProviders().then(setMeta).catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Abertura manual (botão no Dashboard) — reavalia o estado e pula pro 1º passo pendente.
    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        detect().then(({ exList, aiList, stratHas }) => {
            setStep(firstPendingStep(exList, aiList, stratHas));
            setVisible(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openTrigger]);

    function close() {
        localStorage.setItem(scopedStorageKey, '1');
        setVisible(false);
        if (onClose) onClose();
    }

    function requiresPassphrase(exchangeName) {
        const m = (meta.exchanges || []).find(e => e.name === exchangeName);
        return !!(m && m.requiresPassphrase) || PASSPHRASE_EXCHANGES.includes(exchangeName);
    }

    async function handleSaveExchange() {
        setExError('');
        if (!exForm.exchangeName) return setExError('Selecione a corretora.');
        if (!exForm.accessKey || !exForm.secretKey) return setExError('Preencha a Access Key e a Secret Key.');

        setExSaving(true);
        try {
            const saved = await createUserExchange({
                exchangeName: exForm.exchangeName,
                accessKey: exForm.accessKey,
                secretKey: exForm.secretKey,
                passphrase: exForm.passphrase || undefined,
                isPrimary: true
            });
            await detect();
            setExSaving(false);
            setExTesting(true);
            try {
                const r = await testUserExchange(saved.id, 'production');
                setExResult({ ok: true, message: _msg(r?.message, 'Conexão confirmada.') });
            } catch (err) {
                setExResult({ ok: false, message: _errMsg(err, 'Não foi possível validar a conexão agora — revise as chaves em Configurações → Corretora.') });
            } finally {
                setExTesting(false);
            }
        } catch (err) {
            setExError(_errMsg(err, 'Erro ao salvar a corretora.'));
            setExSaving(false);
        }
    }

    async function handleSaveTestCreds() {
        if (!primaryExchange) return;
        setTestError('');
        if (!testForm.testAccessKey || !testForm.testSecretKey) return setTestError('Preencha as duas chaves de teste (ou clique em Pular).');

        setTestSaving(true);
        try {
            await updateUserExchange(primaryExchange.id, {
                testAccessKey: testForm.testAccessKey,
                testSecretKey: testForm.testSecretKey,
                testPassphrase: testForm.testPassphrase || undefined,
                testEnabled: true
            });
            await detect();
            setTestResult({ ok: true, message: 'Credenciais de testnet salvas.' });
        } catch (err) {
            setTestError(_errMsg(err, 'Erro ao salvar as credenciais de teste.'));
        } finally {
            setTestSaving(false);
        }
    }

    function handleSkipAi() {
        localStorage.setItem(scopedAiSkipKey, '1');
        setStep(3);
    }

    async function handleSaveAi() {
        setAiError('');
        if (!aiForm.provider) return setAiError('Selecione o provedor de IA.');
        if (!aiForm.apiKey) return setAiError('Cole a API Key.');

        setAiSaving(true);
        try {
            const saved = await createAiProvider({
                provider: aiForm.provider,
                apiKey: aiForm.apiKey,
                model: aiForm.model || undefined,
                role: 'primary'
            });
            await detect();
            setAiSaving(false);
            setAiTesting(true);
            try {
                const r = await testAiProvider(saved.id);
                setAiResult({ ok: true, message: _msg(r?.message, 'IA respondeu com sucesso.') });
            } catch (err) {
                setAiResult({ ok: false, message: _errMsg(err, 'Não foi possível validar agora — revise a API Key em Configurações → IA.') });
            } finally {
                setAiTesting(false);
            }
        } catch (err) {
            setAiError(_errMsg(err, 'Erro ao salvar o provedor de IA.'));
            setAiSaving(false);
        }
    }

    function handleSkipStrategy() {
        localStorage.setItem(scopedStrategySkipKey, '1');
        close();
    }

    if (!ready || !visible) return null;

    const exchangeMetaOf = name => (meta.exchanges || []).find(e => e.name === name);
    const aiMetaOf = name => (meta.aiProviders || []).find(p => p.name === name);
    const primaryExchangeMeta = primaryExchange ? exchangeMetaOf(primaryExchange.exchangeName) : null;
    const activeAi = aiProvidersList.find(p => p.role === 'primary' && p.isActive) || aiProvidersList.find(p => p.isActive) || aiProvidersList[0] || null;
    const activeAiMeta = activeAi ? aiMetaOf(activeAi.provider) : null;

    function renderDots() {
        const done = [hasExchange, true, hasAi, hasStrategy];
        return (
            <div className='d-flex justify-content-center mb-4 flex-wrap'>
                {STEP_LABELS.map((label, i) => (
                    <React.Fragment key={i}>
                        <div className='text-center' style={{ minWidth: 78, cursor: 'pointer' }} onClick={() => setStep(i)}>
                            <div className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-1 ${i <= step ? 'bg-primary text-white' : 'bg-gray-200 text-muted'}`}
                                style={{ width: 30, height: 30, fontWeight: 'bold' }}>
                                {done[i] ? '✓' : i + 1}
                            </div>
                            <div style={{ fontSize: 11 }} className={i === step ? 'text-primary fw-bold' : 'text-muted'}>{label}</div>
                        </div>
                        {i < STEP_LABELS.length - 1 && (
                            <div className='flex-grow-1 d-flex align-items-center pb-3' style={{ minWidth: 20 }}>
                                <div style={{ height: 2, width: '100%', background: i < step ? '#0d6efd' : '#dee2e6' }} />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    function renderCorretora() {
        if (hasExchange) {
            return (
                <div>
                    <div className='alert alert-success py-2 d-flex justify-content-between align-items-center'>
                        <span>✓ Corretora conectada: <strong>{primaryExchangeMeta ? primaryExchangeMeta.label : primaryExchange.exchangeName}</strong></span>
                        <Link to='/settings' className='btn btn-sm btn-outline-success'>Gerenciar</Link>
                    </div>
                    {exResult && (
                        <div className={`alert py-2 small mb-0 ${exResult.ok ? 'alert-success' : 'alert-warning'}`}>
                            {exResult.ok ? '✓ ' : '⚠️ '}{exResult.message}
                        </div>
                    )}
                    <small className='text-muted d-block mt-2'>
                        Você pode trocar ou adicionar outras corretoras em <strong>Configurações → Corretora</strong>.
                    </small>
                </div>
            );
        }
        return (
            <div>
                <p className='text-muted mb-2'>
                    Para o bot operar, conecte a sua conta de uma corretora com uma chave de API (permissão de leitura + negociação).
                    {isFutures && ' Esta chave também é usada como base para o mercado de Futuros — configure as chaves específicas de Futuros depois, em Configurações → Corretora.'}
                </p>
                <SharedExchangeSecurity />
                <div className='mb-2'>
                    <label className='form-label'>Corretora</label>
                    <select className='form-select form-select-sm' value={exForm.exchangeName}
                        onChange={e => setExForm({ ...exForm, exchangeName: e.target.value })}>
                        <option value=''>Selecionar...</option>
                        <optgroup label='⭐ Recomendadas'>
                            {(meta.exchanges || []).filter(e => e.recommended).map(e => (
                                <option key={e.name} value={e.name}>{e.label}</option>
                            ))}
                        </optgroup>
                        <optgroup label='Outras corretoras'>
                            {(meta.exchanges || []).filter(e => !e.recommended).map(e => (
                                <option key={e.name} value={e.name}>{e.label}</option>
                            ))}
                        </optgroup>
                    </select>
                </div>
                {exForm.exchangeName && EXCHANGE_GUIDES[exForm.exchangeName] && (
                    <CredentialsGuide guide={EXCHANGE_GUIDES[exForm.exchangeName]} />
                )}
                <div className='row'>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label'>Access Key</label>
                        <input className='form-control form-control-sm' value={exForm.accessKey}
                            placeholder='Cole sua Access Key aqui'
                            onChange={e => setExForm({ ...exForm, accessKey: e.target.value })} />
                    </div>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label'>Secret Key</label>
                        <input type='password' className='form-control form-control-sm' value={exForm.secretKey}
                            placeholder='Cole sua Secret Key aqui'
                            onChange={e => setExForm({ ...exForm, secretKey: e.target.value })} />
                    </div>
                </div>
                {requiresPassphrase(exForm.exchangeName) && (
                    <div className='mb-2'>
                        <label className='form-label'>Passphrase</label>
                        <input type='password' className='form-control form-control-sm' value={exForm.passphrase}
                            placeholder='Passphrase'
                            onChange={e => setExForm({ ...exForm, passphrase: e.target.value })} />
                    </div>
                )}
                {exError && <div className='alert alert-danger py-2 small mb-0'>{exError}</div>}
            </div>
        );
    }

    function renderAmbiente() {
        if (!hasExchange) {
            return (
                <div className='alert alert-info py-2 small mb-0'>
                    Configure a corretora no passo anterior primeiro — as credenciais de teste ficam associadas a ela.
                </div>
            );
        }
        return (
            <div>
                <p className='text-muted mb-2'>
                    O Hydra opera em dois ambientes: <strong>Produção</strong> (dinheiro real, o que você acabou de configurar) e{' '}
                    <strong>Teste (testnet)</strong> — uma conta de simulação da própria corretora, ideal para validar estratégias sem risco antes
                    de ligar em produção.
                </p>
                <p className='text-muted small mb-3'>
                    Você está navegando no ambiente <strong>{environment === 'test' ? 'Teste' : 'Produção'}</strong> agora
                    (troque a qualquer momento no menu lateral).
                </p>
                {hasTestCreds ? (
                    <div className='alert alert-success py-2 small mb-0'>
                        ✓ Credenciais de testnet já configuradas para {primaryExchangeMeta ? primaryExchangeMeta.label : primaryExchange.exchangeName}.
                        Gerencie em <strong>Configurações → Corretora</strong>.
                    </div>
                ) : !wantsTestnet ? (
                    <button type='button' className='btn btn-outline-primary btn-sm' onClick={() => setWantsTestnet(true)}>
                        Quero configurar credenciais de teste (testnet)
                    </button>
                ) : (
                    <div>
                        <div className='row'>
                            <div className='col-md-6 mb-2'>
                                <label className='form-label'>Test Access Key</label>
                                <input className='form-control form-control-sm' value={testForm.testAccessKey}
                                    onChange={e => setTestForm({ ...testForm, testAccessKey: e.target.value })} />
                            </div>
                            <div className='col-md-6 mb-2'>
                                <label className='form-label'>Test Secret Key</label>
                                <input type='password' className='form-control form-control-sm' value={testForm.testSecretKey}
                                    onChange={e => setTestForm({ ...testForm, testSecretKey: e.target.value })} />
                            </div>
                        </div>
                        {requiresPassphrase(primaryExchange.exchangeName) && (
                            <div className='mb-2'>
                                <label className='form-label'>Test Passphrase</label>
                                <input type='password' className='form-control form-control-sm' value={testForm.testPassphrase}
                                    onChange={e => setTestForm({ ...testForm, testPassphrase: e.target.value })} />
                            </div>
                        )}
                        {testError && <div className='alert alert-danger py-2 small'>{testError}</div>}
                        {testResult && <div className='alert alert-success py-2 small'>✓ {testResult.message}</div>}
                        <button type='button' className='btn btn-primary btn-sm' disabled={testSaving} onClick={handleSaveTestCreds}>
                            {testSaving ? 'Salvando...' : 'Salvar credenciais de teste'}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    function renderIa() {
        if (hasAi) {
            return (
                <div>
                    <div className='alert alert-success py-2 d-flex justify-content-between align-items-center'>
                        <span>
                            ✓ IA configurada: <strong>{activeAiMeta ? activeAiMeta.label : (activeAi && activeAi.provider)}</strong>
                        </span>
                        <Link to='/settings' className='btn btn-sm btn-outline-success'>Gerenciar</Link>
                    </div>
                    {aiResult && (
                        <div className={`alert py-2 small mb-0 ${aiResult.ok ? 'alert-success' : 'alert-warning'}`}>
                            {aiResult.ok ? '✓ ' : '⚠️ '}{aiResult.message}
                        </div>
                    )}
                </div>
            );
        }
        const curated = AI_MODELS[aiForm.provider] || [];
        return (
            <div>
                <p className='text-muted mb-2'>
                    Opcional: conecte um provedor de IA para receber sinais inteligentes de compra/venda com base em análise de mercado
                    (candles, indicadores e regime). Sem IA, o bot ainda opera com as estratégias baseadas em regras (EMA, grid, DCA...).
                </p>
                <div className='row'>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label'>Provedor</label>
                        <select className='form-select form-select-sm' value={aiForm.provider}
                            onChange={e => setAiForm({ ...aiForm, provider: e.target.value, model: '' })}>
                            <option value=''>Selecionar...</option>
                            {(meta.aiProviders || []).map(p => (
                                <option key={p.name} value={p.name}>
                                    {p.tier === 'FREE_TIER' ? '🟢' : '💲'} {p.label}{p.recommended ? ' ★' : ''}
                                </option>
                            ))}
                        </select>
                        {aiForm.provider && aiMetaOf(aiForm.provider) && (
                            <small className='text-muted d-block mt-1'>ℹ️ {aiMetaOf(aiForm.provider).note}</small>
                        )}
                    </div>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label'>Modelo</label>
                        <select className='form-select form-select-sm' value={aiForm.model}
                            onChange={e => setAiForm({ ...aiForm, model: e.target.value })}>
                            <option value=''>Padrão do provedor</option>
                            {curated.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>
                </div>
                {aiForm.provider && AI_GUIDES[aiForm.provider] && <CredentialsGuide guide={AI_GUIDES[aiForm.provider]} />}
                <div className='mb-2'>
                    <label className='form-label'>API Key</label>
                    <input type='password' className='form-control form-control-sm' value={aiForm.apiKey}
                        placeholder='Cole a API Key aqui'
                        onChange={e => setAiForm({ ...aiForm, apiKey: e.target.value })} />
                    <small className='text-muted'>Armazenada de forma criptografada (AES).</small>
                </div>
                {aiError && <div className='alert alert-danger py-2 small mb-0'>{aiError}</div>}
            </div>
        );
    }

    function renderEstrategia() {
        const marketLabel = isFutures ? 'Futuros' : 'Spot';
        const recommendedLabel = isFutures ? 'Short Direcional' : 'Setup AI Full';
        return (
            <div>
                <p className='text-muted mb-2'>
                    Última etapa: crie sua primeira estratégia. Para o mercado <strong>{marketLabel}</strong> em que você está agora,
                    a recomendação é <strong>{recommendedLabel}</strong>, no modo automático (o bot escolhe os ativos).
                </p>
                {hasStrategy ? (
                    <div className='alert alert-success py-2 d-flex justify-content-between align-items-center'>
                        <span>✓ Você já tem estratégia neste mercado/ambiente.</span>
                        <Link to='/strategies' className='btn btn-sm btn-outline-success'>Ver estratégias</Link>
                    </div>
                ) : (
                    <div className='alert alert-info py-2 small'>Você ainda não tem estratégia neste mercado/ambiente.</div>
                )}
                <div className='mt-2'>
                    <StrategyWizard />
                </div>
            </div>
        );
    }

    function renderFooter() {
        return (
            <div className='modal-footer d-flex justify-content-between'>
                <div>
                    {step > 0 && <button className='btn btn-outline-secondary btn-sm' onClick={() => setStep(step - 1)}>Voltar</button>}
                </div>
                <div className='d-flex gap-2'>
                    {step === 0 && !hasExchange && (
                        <button className='btn btn-primary btn-sm' disabled={exSaving || exTesting} onClick={handleSaveExchange}>
                            {exSaving ? 'Salvando...' : exTesting ? 'Testando conexão...' : 'Salvar e testar'}
                        </button>
                    )}
                    {step === 0 && hasExchange && (
                        <button className='btn btn-primary btn-sm' onClick={() => setStep(1)}>Próximo</button>
                    )}
                    {step === 1 && (
                        <button className='btn btn-primary btn-sm' onClick={() => setStep(2)}>
                            {hasTestCreds || !wantsTestnet ? 'Próximo' : 'Pular por enquanto'}
                        </button>
                    )}
                    {step === 2 && !hasAi && (
                        <>
                            <button className='btn btn-outline-secondary btn-sm' onClick={handleSkipAi}>Pular por enquanto</button>
                            <button className='btn btn-primary btn-sm' disabled={aiSaving || aiTesting} onClick={handleSaveAi}>
                                {aiSaving ? 'Salvando...' : aiTesting ? 'Testando...' : 'Salvar'}
                            </button>
                        </>
                    )}
                    {step === 2 && hasAi && (
                        <button className='btn btn-primary btn-sm' onClick={() => setStep(3)}>Próximo</button>
                    )}
                    {step === 3 && (
                        <button className='btn btn-success btn-sm' onClick={handleSkipStrategy}>Concluir</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='modal d-block' tabIndex='-1' style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className='modal-dialog modal-dialog-centered modal-lg'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>🧭 Assistente de configuração</h5>
                        <button type='button' className='btn-close' onClick={close} />
                    </div>
                    <div className='modal-body'>
                        {renderDots()}
                        {step === 0 && renderCorretora()}
                        {step === 1 && renderAmbiente()}
                        {step === 2 && renderIa()}
                        {step === 3 && renderEstrategia()}
                    </div>
                    {renderFooter()}
                </div>
            </div>
        </div>
    );
}

export default SetupAssistant;
